import React, { useMemo } from 'react';

import { Box } from '@wds/box';
import { logAmplitudeEvent } from '~app/pages/Pricing/pages/shared';
import { usePricingState } from '~shared/hooks/usePricingState';
import { Packages } from '~shared/constants/pricing';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import ACTIONS from '~shared/actions';
import { AMPLITUDE_PAGE_EVENTS } from '~shared/util';
import { CombinedActions } from '~shared/types/pricing/actions';
import { Grow2792ToggleProps } from '../../types';
import { PACKAGE_SWAP } from '../../constants';

import useStyles from './useStyles';

const allowedTogglePackages = [Packages.FormsAnnual, Packages.AdvantageAnnual, Packages.TeamAdvantage];

export default function ToggleSwitch({ packageName }: Readonly<Grow2792ToggleProps>): JSX.Element {
  const { dispatch } = usePricingState();

  const { Switch, Values, Selected, NotSelected } = useStyles();
  const { pageName } = useTrackingDetails();

  function handleChange(): void {
    const packageSwap = {
      packageToBeReplaced: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].id,
      replacedWith: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.id,
    };
    dispatch({ type: ACTIONS.SWITCH_PACKAGES, payload: packageSwap } as CombinedActions);

    if (packageName === Packages.TeamAdvantage) {
      dispatch({ type: ACTIONS.PNP_REFRESH_RESET_TOGGLE_SWITCH } as CombinedActions);
    }

    logAmplitudeEvent({
      eventName: 'pricing clicked toggle',
      pageName,
      amplitudeEvent: AMPLITUDE_PAGE_EVENTS.experimentEvent,
      otherData: {
        selectedPackage: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.packageName,
      },
    });
  }

  const isChecked = useMemo(() => allowedTogglePackages?.includes(packageName as Packages), [packageName]);

  const monthlyPackages = [Packages.AdvantageMonthly, Packages.FormsMonthly];
  const annualPackages = [Packages.AdvantageAnnual, Packages.FormsAnnual, Packages.TeamAdvantage];

  const monthlySelected = monthlyPackages.includes(packageName as Packages) ? Selected : NotSelected;
  const annualSelected = annualPackages.includes(packageName as Packages) ? Selected : NotSelected;

  const id = `${packageName}-toggle`;

  return (
    <>
      <label htmlFor={id} data-testid={`ToggleSwitch__${packageName}`}>
        <Box className={Switch}>
          <input
            role="switch"
            aria-checked="false"
            id={id}
            tabIndex={0}
            type="checkbox"
            defaultChecked={isChecked}
            onChange={handleChange}
          />
          <Box className={Values}>
            <span className={monthlySelected}>Monthly</span>
            <span className={annualSelected}>Annual</span>
          </Box>
        </Box>
      </label>
    </>
  );
}
